import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Container, Row, Col, ProgressBar } from 'react-bootstrap';
import { getUserProfile, updateProfile } from '../services/api';

function Profile() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState('');

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await getUserProfile();
        const userData = response.data;
        
        setUsername(userData.username);
        setEmail(userData.email);
        setError('');
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('Failed to load profile data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Password strength calculation
  useEffect(() => {
    if (!newPassword) {
      setPasswordStrength(0);
      setPasswordFeedback('');
      return;
    }

    // Criteria for password strength
    const hasMinLength = newPassword.length >= 8;
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasLowercase = /[a-z]/.test(newPassword);
    const hasNumbers = /[0-9]/.test(newPassword);
    const hasSpecialChars = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword);

    // Calculate strength score (0-100)
    let strength = 0;
    if (hasMinLength) strength += 20;
    if (hasUppercase) strength += 20;
    if (hasLowercase) strength += 20;
    if (hasNumbers) strength += 20;
    if (hasSpecialChars) strength += 20;

    setPasswordStrength(strength);

    // Provide feedback
    if (strength < 40) {
      setPasswordFeedback('Weak password');
    } else if (strength < 80) {
      setPasswordFeedback('Moderate password');
    } else {
      setPasswordFeedback('Strong password');
    }
  }, [newPassword]);

  // Get progress bar variant based on strength
  const getProgressBarVariant = () => {
    if (passwordStrength < 40) return 'danger';
    if (passwordStrength < 80) return 'warning';
    return 'success';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email address is required');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setError('');
    setSuccess('');
    setUpdating(true);
    
    try {
      // Prepare update data
      const updateData = { email };
      
      // Add password to update data if password change is enabled
      if (showPasswordChange) {
        // Validate password fields
        if (!currentPassword) {
          setError('Current password is required');
          setUpdating(false);
          return;
        }
        
        if (!newPassword) {
          setError('New password is required');
          setUpdating(false);
          return;
        }
        
        if (newPassword !== confirmPassword) {
          setError('New password and confirmation do not match');
          setUpdating(false);
          return;
        }
        
        if (newPassword.length < 8) {
          setError('Password must be at least 8 characters long');
          setUpdating(false);
          return;
        }

        // Password strength validation
        if (passwordStrength < 60) {
          setError('Please use a stronger password. Include uppercase, lowercase, numbers, and special characters.');
          setUpdating(false);
          return;
        }
        
        // Add password fields to update data
        updateData.currentPassword = currentPassword;
        updateData.password = newPassword;
      }
      
      const response = await updateProfile(updateData);
      setSuccess('Profile updated successfully!');
      
      // Update the displayed data with the response data
      setEmail(response.data.email);
      
      // Reset password fields if update was successful
      if (showPasswordChange) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordChange(false);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to update profile. Please try again.');
      }
    } finally {
      setUpdating(false);
    }
  };
  
  const togglePasswordChange = () => {
    setShowPasswordChange(!showPasswordChange);
    // Reset password fields and errors when toggling
    if (!showPasswordChange) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
    }
  };
  
  return (
    <Container>
      <h1 className="my-4">My Profile</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      {loading ? (
        <div className="text-center py-4">Loading profile data...</div>
      ) : (
        <Row>
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header as="h5">
                <i className="bi bi-person-fill me-2"></i>
                Personal Information
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      value={username}
                      disabled
                      readOnly
                      className="form-control-dark-disabled"
                    />
                    <Form.Text className="text-muted">
                      Username cannot be changed
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label>Email address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Form.Group>
                  
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={updating}
                    >
                      {updating ? 'Updating...' : 'Save Changes'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header as="h5">
                <i className="bi bi-lock-fill me-2"></i>
                Password Management
              </Card.Header>
              <Card.Body>
                {!showPasswordChange ? (
                  <div className="text-center py-3">
                    <p className="mb-3">Change your account password here</p>
                    <Button 
                      variant="outline-primary" 
                      onClick={togglePasswordChange}
                    >
                      <i className="bi bi-key me-2"></i>
                      Change Password
                    </Button>
                  </div>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Current Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      {newPassword && (
                        <>
                          <ProgressBar 
                            className="mt-2" 
                            now={passwordStrength} 
                            variant={getProgressBarVariant()} 
                          />
                          <Form.Text className={`text-${getProgressBarVariant()}`}>
                            {passwordFeedback}
                          </Form.Text>
                          <Form.Text className="text-muted mt-2">
                            Password must be at least 8 characters long and include uppercase, lowercase, 
                            numbers, and special characters.
                          </Form.Text>
                        </>
                      )}
                    </Form.Group>
                    
                    <Form.Group className="mb-4">
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </Form.Group>
                    
                    <div className="d-flex justify-content-between">
                      <Button 
                        variant="outline-secondary" 
                        onClick={togglePasswordChange}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={updating}
                      >
                        {updating ? 'Updating...' : 'Save Password'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default Profile; 